import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Copy, Link2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { unterschriftAPI } from '@/lib/api';

/* Upsell per QR-Code oder Link (22.10.2026, seit 26.10.2026 derselbe Vorgang): gültig 24 Stunden und läuft im HINTERGRUND —
   das Fenster darf zu, ihr arbeitet weiter; unterschreibt das Mitglied, steht die Vereinbarung an der Mitgliedschaft. Der Link
   wird hier EINMAL gezeigt (das Token speichert der Server nicht im Klartext). 4 Stunden vor Ablauf bekommt das Mitglied eine
   E-Mail mit dem Link (unterschrift/kern.erinnern_lauf). Zurückziehen = derselbe Abbruch wie an der Station. */
const zeit = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–');
const ABFRAGE_MS = 4000;

export function linkGueltigBis(iso) { return zeit(iso); }

export default function LinkVersendet({ vorgang, onFertig, onZurueckgezogen, onUnterschrieben }) {
  const [busy, setBusy] = useState(false);
  const [stand, setStand] = useState(vorgang);
  // Solange das Fenster offen ist, den Stand nachsehen — schließen bricht nichts ab
  useEffect(() => {
    if (!['offen', 'angezeigt', 'wird_abgeschlossen'].includes(stand.status)) return undefined;
    const t = setInterval(async () => {
      try {
        const r = (await unterschriftAPI.status(vorgang.id)).data;
        setStand((s) => ({ ...s, ...r }));
        if (r.status === 'unterschrieben' && r.ergebnis) onUnterschrieben?.(r.ergebnis);
      } catch { /* Netz kurz weg: nächste Abfrage */ }
    }, ABFRAGE_MS);
    return () => clearInterval(t);
  }, [stand.status, vorgang.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const kopieren = async () => {
    try { await navigator.clipboard.writeText(vorgang.url); toast.success('Link kopiert'); }
    catch {
      const feld = document.getElementById(`link-${vorgang.id}`);
      feld?.select(); toast.message('Link markiert — bitte kopieren');
    }
  };
  const zurueckziehen = async () => {
    setBusy(true);
    try {
      const r = (await unterschriftAPI.abbrechen(vorgang.id, 'Link zurückgezogen')).data;
      if (r.status === 'unterschrieben' || r.status === 'wird_abgeschlossen') toast.message('Schon unterschrieben — nichts zurückgezogen');
      else { toast.success('QR-Code und Link zurückgezogen'); onZurueckgezogen?.(); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Zurückziehen fehlgeschlagen'); }
    setBusy(false);
  };

  if (stand.status === 'unterschrieben') {
    return (
      <div className="space-y-3 text-sm" data-testid="link-unterschrieben">
        <p className="font-medium flex items-center gap-2 text-emerald-700 dark:text-emerald-400"><CheckCircle2 className="w-4 h-4" /> {stand.kunde} hat unterschrieben.</p>
        <div className="flex justify-end"><Button size="sm" onClick={onFertig}>Fertig</Button></div>
      </div>
    );
  }
  const laeuft = ['offen', 'angezeigt', 'wird_abgeschlossen'].includes(stand.status);
  return (
    <div className="space-y-3 text-sm" data-testid="link-versendet">
      <p className="font-medium flex items-center gap-2"><Link2 className="w-4 h-4 text-marke" /> {stand.kunde} — gültig bis {zeit(stand.laeuft_ab_am)}</p>
      {laeuft ? (
        <>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {vorgang.qr && <img src={vorgang.qr} alt="QR-Code zum Unterschreiben" className="w-44 h-44 rounded-lg border border-border bg-white flex-shrink-0" data-testid="link-qr" />}
            <div className="space-y-2 min-w-0 w-full">
              <p className="text-xs text-muted-foreground">Das Mitglied scannt den Code mit dem eigenen Handy — hier im Studio oder später. Oder schick ihm den Link selbst, z. B. per WhatsApp.</p>
              <div className="flex gap-2">
                <input id={`link-${vorgang.id}`} readOnly value={vorgang.url} onFocus={e => e.target.select()}
                  className="h-9 flex-1 min-w-0 rounded-md border border-input bg-muted/40 px-2 text-xs" data-testid="link-url" />
                <Button size="sm" onClick={kopieren} className="gap-1.5 bg-marke text-white hover:bg-marke-dunkel" data-testid="link-kopieren"><Copy className="w-4 h-4" /> Kopieren</Button>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {stand.status === 'angezeigt' ? 'Das Mitglied hat den Code geöffnet. ' : ''}
            Du kannst das Fenster schließen und weiterarbeiten — der Code bleibt 24 Stunden gültig, die Vereinbarung erscheint an der
            Mitgliedschaft, sobald unterschrieben ist. 4 Stunden vor Ablauf bekommt das Mitglied eine E-Mail mit dem Link.
            Weil auch außerhalb des Studios unterschrieben werden kann, gilt ein Widerrufsrecht von 14 Tagen.
          </p>
        </>
      ) : (
        <p className="text-amber-700 dark:text-amber-400" data-testid="link-beendet">{stand.status === 'abgelaufen' ? 'Abgelaufen — bitte neu erzeugen.' : `Beendet${stand.grund ? `: ${stand.grund}` : ''}.`}</p>
      )}
      <div className="flex justify-end gap-2">
        {laeuft && <Button size="sm" variant="outline" onClick={zurueckziehen} disabled={busy} data-testid="link-zurueckziehen">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Zurückziehen'}</Button>}
        <Button size="sm" onClick={onFertig} data-testid="link-fertig">{laeuft ? 'Weiterarbeiten' : 'Schließen'}</Button>
      </div>
    </div>
  );
}
